import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Bike, Sparkles, Clock, Truck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';

// Background image for delivery section
const DELIVERY_BG = "https://images.pexels.com/photos/4393426/pexels-photo-4393426.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const DeliverySection = () => {
  const { t, language } = useLanguage();

  const platforms = [
    {
      name: 'Uber Eats',
      color: 'from-[#06C167] to-[#05a858]',
      borderColor: 'border-[#06C167]/30',
      hoverBorder: 'hover:border-[#06C167]/60',
      url: 'https://www.ubereats.com/de/store/hanse-pizza/cTNFBxdnR9mVVXQv3W28bg',
    },
    {
      name: 'Lieferando',
      color: 'from-[#FF8000] to-[#e67300]',
      borderColor: 'border-[#FF8000]/30',
      hoverBorder: 'hover:border-[#FF8000]/60',
      url: 'https://www.lieferando.de',
    },
    {
      name: 'Wolt',
      color: 'from-[#009DE0] to-[#0088c4]',
      borderColor: 'border-[#009DE0]/30',
      hoverBorder: 'hover:border-[#009DE0]/60',
      url: 'https://wolt.com',
    },
  ];

  return (
    <section 
      id="delivery" 
      className="relative overflow-hidden"
      data-testid="delivery-section"
    >
      {/* Premium Section Header with Background Image */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${DELIVERY_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-hanse-dark/60 via-hanse-dark/70 to-hanse-dark/95" />
        
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          {/* Sparkle Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex items-center gap-2 mb-4 bg-gradient-to-r from-emerald-500/20 to-emerald-600/20 backdrop-blur-sm px-4 py-2 rounded-full border border-emerald-400/30"
          >
            <Sparkles className="w-5 h-5 text-emerald-400" />
            <span className="text-emerald-300 font-semibold text-sm uppercase tracking-wider">
              {language === 'de' ? 'Schnell & Zuverlässig' : 'Fast & Reliable'}
            </span>
            <Sparkles className="w-5 h-5 text-emerald-400" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3"
          >
            {t('delivery.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white/80 text-base md:text-lg max-w-2xl"
          >
            {t('delivery.subtitle')}
          </motion.p>
        </div>
      </div>

      {/* Premium Content */}
      <div className="bg-gradient-to-b from-hanse-dark to-hanse-navy py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          
          {/* Platform Cards - Premium Style */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-10">
            {platforms.map((platform, index) => (
              <motion.div
                key={platform.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => window.open(platform.url, '_blank')}
                className={`group cursor-pointer bg-white/5 backdrop-blur-sm rounded-2xl p-6 border ${platform.borderColor} ${platform.hoverBorder} hover:bg-white/10 transition-all duration-300`}
                data-testid={`delivery-platform-${platform.name.toLowerCase().replace(' ', '-')}`}
              >
                <div className={`w-full h-2 rounded-full bg-gradient-to-r ${platform.color} mb-5`}></div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3 group-hover:text-amber-300 transition-colors">
                  {platform.name}
                </h3>
                <p className="text-white/50 text-sm mb-5">
                  {language === 'de' ? 'Jetzt bestellen über' : 'Order now via'} {platform.name}
                </p>
                <Button
                  className={`w-full bg-gradient-to-r ${platform.color} text-white font-semibold py-3 rounded-xl hover:opacity-90 transition-opacity`}
                >
                  {t('delivery.orderNow')}
                </Button>
              </motion.div>
            ))}
          </div>

          {/* Call Us Banner - Premium */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-hanse-red/20 to-hanse-red/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-hanse-red/20 mb-8"
            data-testid="call-us-banner"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-center md:text-left">
                <div className="w-14 h-14 md:w-16 md:h-16 bg-hanse-red/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-7 h-7 md:w-8 md:h-8 text-hanse-red" />
                </div>
                <div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-1">
                    {t('delivery.callTitle')}
                  </h3>
                  <p className="text-amber-400 text-xl md:text-2xl font-bold">+49 421 48535145</p>
                </div>
              </div>
              <Button
                onClick={() => window.location.href = 'tel:+4942148535145'}
                className="bg-hanse-red hover:bg-hanse-red/90 text-white font-bold px-8 py-4 rounded-full text-base md:text-lg w-full md:w-auto"
                data-testid="call-order-btn"
              >
                {t('delivery.callBtn')}
              </Button>
            </div>
          </motion.div>

          {/* Info Badges - Premium Style */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 flex items-center gap-3"
            >
              <div className="w-10 h-10 bg-hanse-red/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Bike className="w-5 h-5 text-hanse-red" />
              </div>
              <span className="text-white/80 font-medium text-sm">{t('delivery.selfDelivery')}</span>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 flex items-center gap-3"
            >
              <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5 text-amber-400" />
              </div>
              <span className="text-white/80 font-medium text-sm">{t('delivery.minOrder')}</span>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-amber-500/10 backdrop-blur-sm rounded-xl p-4 border border-amber-400/20 flex items-center gap-3"
            >
              <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-amber-400 font-bold text-lg">€</span>
              </div>
              <span className="text-amber-300 font-medium text-sm">{t('delivery.freeDelivery')}</span>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 flex items-center gap-3"
            >
              <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="text-white/80 text-sm">
                <span className="font-medium block">Mo-So 11-22</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeliverySection;
